import { defineStore } from 'pinia'
import { useConfigStore } from './configStore'
import { storeToRefs } from 'pinia'

export const useSubscriptionStore = defineStore('subscription', () => {
  const configStore = useConfigStore()
  const { owner } = storeToRefs(configStore)

  // Computed properties to access owner attributes
  const stripeStatus = computed(() => owner.value?.stripe_status)
  const trialEndsAt = computed(() => owner.value?.trial_ends_at)
  const isFreeAccount = computed(() => owner.value?.free_account)
  const isAdmin = computed(() => owner.value?.is_admin)
  const activeSubscription = computed(() => {
    return owner.value?.active_subscription
  })

  const hasValidSubscription = computed(() => {
    const subscription = owner.value?.active_subscription
    if (subscription) {
      if (subscription.stripe_status !== 'active') return false
      return !subscription.ends_at
    }

    return false
  })

  /**
   * Determines if the user has an active subscription
   * Active users are either:
   * 1. Free or admin accounts
   * 2. Have an active or trialing stripe subscription
   *
   * Returns true while owner data is loading to prevent
   * flashing "inactive" state before data is available.
   */
  const isActive = computed(() => {
    // If owner hasn't loaded yet, assume active to prevent flash of inactive state
    if (!owner.value) return true

    if (isTrialing.value) return true
    const activeStripeStatuses = ['trialing', 'active']
    return (
      isFreeAccount.value ||
      isAdmin.value ||
      activeStripeStatuses.includes(stripeStatus.value)
    )
  })

  /**
   * Determines if the user is currently in trial period
   */
  const isTrialing = computed(() => {
    if (owner.value?.on_trial) return true
    return stripeStatus.value === 'trialing'
  })

  const trialExpiresAt = computed(() => {
    if (owner.value?.on_trial) return owner.value?.trial_ends_at
    return null
  })

  /**
   * Whether the user has a payment method on file.
   * Used to distinguish between trial-expired (never paid) and payment-failed (did pay once).
   */
  const hasPaymentMethod = computed(() => owner.value?.has_payment_method ?? false)

  /**
   * Trial has expired and user never subscribed.
   * This is true when the account is inactive AND they have no payment method on file.
   * Note: Some accounts may have an active_subscription record from trial setup,
   * so we use has_payment_method as the definitive signal.
   */
  const isTrialExpired = computed(() => {
    if (!owner.value) return false
    if (isActive.value) return false
    // If they have no payment method, they never actually subscribed
    return !owner.value.has_payment_method
  })

  /**
   * User had a subscription but payment failed.
   * This is true when the account is inactive AND they have a payment method on file
   * (meaning they did subscribe at some point, but payment failed).
   */
  const isPaymentFailed = computed(() => {
    if (!owner.value) return false
    if (isActive.value) return false
    // If they have a payment method, they subscribed but payment failed
    return owner.value.has_payment_method === true
  })

  return {
    stripeStatus,
    trialEndsAt,
    trialExpiresAt,
    isFreeAccount,
    isAdmin,
    isActive,
    isTrialing,
    isTrialExpired,
    isPaymentFailed,
    hasPaymentMethod,
    activeSubscription,
    hasValidSubscription
  }
})
